// file: src/LandlordPages/LandlordSidebar.jsx
import React from "react";
import { motion } from "framer-motion";
import { Home, LogOut, CalendarDays } from "lucide-react"; // ⟵ add CalendarDays
import { Link, useLocation, useNavigate } from "react-router-dom";

const BLUE = "#2A7EFA";
const BLUE_LIGHT = "#5AA9FF";

export default function LandlordSidebar({ onLogout, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const active = (path) => location.pathname.startsWith(path);

  const handleLogout = () => {
    try {
      localStorage.clear();
    } catch {}
    try {
      sessionStorage.clear();
    } catch {}
    try {
      onClose && onClose();
    } catch {}
    try {
      onLogout && onLogout();
    } catch {}
    navigate("/landlord/login", { replace: true });
  };

  const starCSS = `
    @keyframes twinkle1 { 0%,100% {opacity:.9} 50% {opacity:.35}}
    @keyframes twinkle2 { 0%,100% {opacity:.85} 50% {opacity:.4}}
    .twinkle-1 { animation: twinkle1 3.8s ease-in-out infinite; }
    .twinkle-2 { animation: twinkle2 5.4s ease-in-out infinite; }
  `;

  return (
    <aside
      className="
        relative flex h-full w-72 flex-col
        border-r border-black/[0.06] bg-white
        shadow-xl
        md:backdrop-blur-xl md:bg-white/90
      "
      style={{
        backgroundImage:
          "linear-gradient(180deg, rgba(255,255,255,.98), rgba(255,255,255,.92))",
      }}
    >
      {/* Subtle right glow line */}
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-px"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,.06), rgba(42,126,250,.18), rgba(0,0,0,.06))",
        }}
      />

      {/* Brand header card */}
      <div className="px-5 pt-5">
        <div
          className="
            relative flex items-center gap-3 rounded-2xl
            border border-black/[0.06] bg-white
            px-4 py-3 shadow-sm
          "
          style={{
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.6), 0 10px 24px rgba(0,0,0,0.04)",
          }}
        >
          <div
            className="grid h-11 w-11 place-items-center rounded-2xl text-white shadow-lg"
            style={{
              background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
              boxShadow: `0 14px 28px ${BLUE}2e`,
            }}
            aria-label="TowPro"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13" />
              <path
                d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"
                fill="currentColor"
              />
            </svg>
          </div>
          <div className="min-w-0">
            <div className="truncate text-[15px] font-semibold tracking-tight text-slate-900">
              Landlord Dashboard
            </div>
            <div className="text-xs text-slate-500">TowPro Portal</div>
          </div>

          <div className="ml-auto inline-flex items-center gap-1 rounded-full border border-black/[0.06] bg-white px-2 py-1 text-[10px] font-semibold tracking-wide text-slate-600">
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: BLUE }}
            />
            LIVE
          </div>

          <div
            className="pointer-events-none absolute inset-0 rounded-2xl"
            style={{
              boxShadow:
                "0 0 0 1px rgba(255,255,255,.7) inset, 0 -8px 24px rgba(90,169,255,.08) inset",
            }}
          />
        </div>
      </div>

      {/* Section: Navigation */}
      <div className="mt-6 px-4">
        <SectionLabel>Navigation</SectionLabel>
        <nav className="mt-2 flex flex-col gap-1">
          <NavItem
            icon={Home}
            label="Dashboard"
            to="/landlord/dashboard"
            active={active("/landlord/dashboard")}
            onNavigate={onClose}
          />
          {/* NEW: All Bookings */}
          <NavItem
            icon={CalendarDays}
            label="All Bookings"
            to="/landlord/all-bookings"
            active={active("/landlord/all-bookings")}
            onNavigate={onClose}
          />
        </nav>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Decorative star panel */}
      <div className="px-4 pt-6">
        <style dangerouslySetInnerHTML={{ __html: starCSS }} />
        <div
          className="
            relative h-28 w-full overflow-hidden
            rounded-2xl border border-black/[0.06] bg-white
            shadow-sm
          "
          style={{
            backgroundImage:
              "radial-gradient(120% 80% at 0% 100%, rgba(42,126,250,.20), transparent 60%), radial-gradient(120% 80% at 100% 0%, rgba(90,169,255,.16), transparent 60%), linear-gradient(180deg, rgba(255,255,255,.9), rgba(255,255,255,.8))",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,.9), 0 10px 24px rgba(0,0,0,.03)",
          }}
          aria-hidden
        >
          <div
            className="absolute inset-0 twinkle-1"
            style={{
              backgroundImage:
                "radial-gradient(1px 1px at 20% 30%, #9fb9ff 60%, transparent), radial-gradient(1.2px 1.2px at 60% 25%, #c7d7ff 60%, transparent), radial-gradient(1.2px 1.2px at 80% 55%, #d8e2ff 60%, transparent), radial-gradient(1px 1px at 35% 70%, #b9ccff 60%, transparent)",
              opacity: 0.9,
            }}
          />
          <div
            className="absolute inset-0 twinkle-2"
            style={{
              backgroundImage:
                "radial-gradient(1.3px 1.3px at 15% 65%, #a7c5ff 60%, transparent), radial-gradient(1.1px 1.1px at 45% 40%, #cddaff 60%, transparent), radial-gradient(1.2px 1.2px at 72% 78%, #e3eaff 60%, transparent), radial-gradient(1px 1px at 90% 20%, #c2d3ff 60%, transparent)",
              opacity: 0.8,
            }}
          />
          <div className="absolute bottom-2 left-2 text-[10px] tracking-wider text-slate-600">
            2025 • Yourspot Rented
          </div>
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              boxShadow:
                "inset 0 0 0 1px rgba(255,255,255,.8), inset 0 -12px 24px rgba(42,126,250,.06)",
            }}
          />
        </div>
      </div>

      {/* Logout */}
      <div className="px-4 py-6">
        <motion.button
          whileTap={{ scale: 0.985 }}
          whileHover={{ y: -1 }}
          onClick={handleLogout}
          className="
            group inline-flex w-full items-center justify-center gap-2
            rounded-2xl bg-rose-600 px-4 py-3 text-sm font-semibold text-white
            shadow-lg ring-1 ring-rose-500/25 transition
            hover:shadow-rose-600/30 focus:outline-none focus:ring-2 focus:ring-rose-400
          "
          style={{ boxShadow: "0 14px 28px rgba(225,29,72,.22)" }}
        >
          <LogOut className="h-4 w-4" />
          Logout
          <span className="ml-auto inline-flex items-center rounded-full bg-white/15 px-2 py-0.5 text-[10px] tracking-wide">
            ⌘ L
          </span>
        </motion.button>
      </div>
    </aside>
  );
}

/* ---------- Bits ---------- */
function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className="h-1 w-1 rounded-full"
        style={{ background: BLUE, boxShadow: `0 0 0 3px ${BLUE}22` }}
        aria-hidden
      />
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {children}
      </div>
      <div className="ml-auto h-px flex-1 bg-gradient-to-r from-black/10 to-transparent" />
    </div>
  );
}

function NavItem({ icon: Icon, label, to, active, onNavigate }) {
  return (
    <Link to={to} onClick={onNavigate} className="block">
      <motion.div
        whileHover={{ y: -1 }}
        transition={{ type: "spring", stiffness: 420, damping: 30 }}
        className={`
          group relative flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm
          border ${active ? "border-black/[0.06]" : "border-transparent"}
          ${active ? "bg-white shadow-sm" : "hover:bg-black/[.035]"}
        `}
        style={{
          boxShadow: active
            ? "inset 0 1px 0 rgba(255,255,255,.9), 0 10px 22px rgba(0,0,0,.04)"
            : undefined,
        }}
      >
        <div
          className={`absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-full transition-all ${
            active ? "bg-slate-900" : "bg-transparent group-hover:bg-black/20"
          }`}
        />
        <div
          className={`
            grid h-8 w-8 place-items-center rounded-xl border
            ${
              active
                ? "border-black/[0.08] bg-white"
                : "border-black/[0.06] bg-white"
            }
          `}
          style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,.95)" }}
        >
          <Icon
            className={`h-4.5 w-4.5 ${
              active ? "text-slate-900" : "text-slate-700"
            }`}
          />
        </div>
        <span
          className={`font-medium ${
            active ? "text-slate-900" : "text-slate-700"
          }`}
        >
          {label}
        </span>
        {active && (
          <span className="ml-auto inline-flex items-center rounded-full bg-emerald-500/90 px-2 py-0.5 text-[10px] font-semibold text-white">
            Active
          </span>
        )}
      </motion.div>
    </Link>
  );
}
